/**
 * Composição do catálogo na borda (T54, CAT-03).
 *
 * Este arquivo **não tem política própria**, e isso é o ponto: ele só junta as
 * dezessete slices de `papeis/` e reexporta o vocabulário. Toda concessão mora no
 * arquivo do domínio dela, que é o que faz dezoito PRs de domínio tocarem
 * dezoito arquivos distintos em vez de brigarem por um só (`ADR-0003`).
 *
 * Se você veio aqui para mudar quem pode o quê, o arquivo é outro.
 */
import {
  PAPEIS_ACESSO,
  CONCESSOES_BASE_ACESSO,
  concessoesCustomizadasDeAcesso,
} from "../papeis/acesso";
import { PAPEIS_AUTOMACAO } from "../papeis/automacao";
import {
  PAPEIS_BIBLIOTECA,
  CONCESSOES_BASE_BIBLIOTECA,
  concessoesCustomizadasDeBiblioteca,
} from "../papeis/biblioteca";
import { PAPEIS_CONTRATOS, CONCESSOES_BASE_CONTRATOS } from "../papeis/contratos";
import { PAPEIS_FABRICACAO, CONCESSOES_BASE_FABRICACAO } from "../papeis/fabricacao";
import { PAPEIS_FIELD } from "../papeis/field";
import { PAPEIS_JURIDICO, CONCESSOES_BASE_JURIDICO } from "../papeis/juridico";
import { PAPEIS_MATERIAIS, CONCESSOES_BASE_MATERIAIS } from "../papeis/materiais";
import { PAPEIS_OBRA, CONCESSOES_BASE_OBRA } from "../papeis/obra";
import { PAPEIS_POSVENDAS } from "../papeis/posvendas";
import { PAPEIS_PRODUTO } from "../papeis/produto";
import { PAPEIS_PROJETOS } from "../papeis/projetos";
import { PAPEIS_PROJETOS_FABRICA } from "../papeis/projetos_fabrica";
import { PAPEIS_RDO, CONCESSOES_BASE_RDO } from "../papeis/rdo";
import { PAPEIS_SEGUROS } from "../papeis/seguros";
import { PAPEIS_SGI, CONCESSOES_BASE_SGI } from "../papeis/sgi";
import { PAPEIS_TAF } from "../papeis/taf";
import type { RegraCompilada } from "../compilador";
import type { Concessao } from "./vocabulario";

/**
 * Todos os papéis do catálogo, um objeto só.
 *
 * A ordem do spread é a de `DOMINIOS`, e não importa: os nomes de papel são
 * `<dominio>.<nivel>`, então duas slices nunca colidem numa chave. O teste de
 * invariante prova isso — se colidissem, o total de papéis seria menor que a soma
 * das slices.
 */
export const PAPEIS = {
  ...PAPEIS_OBRA,
  ...PAPEIS_RDO,
  ...PAPEIS_MATERIAIS,
  ...PAPEIS_CONTRATOS,
  ...PAPEIS_JURIDICO,
  ...PAPEIS_SGI,
  ...PAPEIS_SEGUROS,
  ...PAPEIS_POSVENDAS,
  ...PAPEIS_PRODUTO,
  ...PAPEIS_TAF,
  ...PAPEIS_PROJETOS,
  ...PAPEIS_PROJETOS_FABRICA,
  ...PAPEIS_FABRICACAO,
  ...PAPEIS_AUTOMACAO,
  ...PAPEIS_FIELD,
  ...PAPEIS_BIBLIOTECA,
  ...PAPEIS_ACESSO,
} as const;

/**
 * Os nomes que o catálogo realmente declara.
 *
 * **Não é `NomePapel`.** `NomePapel` é a GRAMÁTICA — `${Dominio}.${Nivel |
 * Capacidade}` —, e ela aceita 170 combinações, das quais a maioria não existe:
 * `taf.total`, `biblioteca.liberacao`, `acesso.upload`. Este tipo é a lista do
 * que foi declarado, e é ele que faz um papel inventado não compilar.
 *
 * O erro que o TypeScript emite lista os nomes válidos, que é a "sugestão" que a
 * tarefa pede: quem escreve `"obra.admim"` recebe de volta o conjunto inteiro.
 */
export type NomePapelDoCatalogo = keyof typeof PAPEIS;

/**
 * Aceita só um papel declarado. É o ponto onde um typo vira erro de compilação
 * em vez de permissão que nunca concede.
 *
 * Existe como função, e não só como tipo, porque um tipo sozinho não é chamado em
 * lugar nenhum e some numa refatoração. Esta é usada pelo teste de tipo.
 */
export function papel<N extends NomePapelDoCatalogo>(nome: N): (typeof PAPEIS)[N] {
  return PAPEIS[nome];
}

/**
 * O que todo usuário autenticado recebe, antes de qualquer papel.
 *
 * Não é política deste arquivo: cada entrada vem do `CONCESSOES_BASE_<D>` da
 * slice, onde está documentado por que aquela concessão não é de papel. Só quatro
 * domínios têm base; os demais exportam lista vazia por simetria e não aparecem
 * aqui.
 */
export const CONCESSOES_BASE: readonly Concessao[] = [
  ...CONCESSOES_BASE_OBRA,
  ...CONCESSOES_BASE_FABRICACAO,
  ...CONCESSOES_BASE_BIBLIOTECA,
  ...CONCESSOES_BASE_ACESSO,
  ...CONCESSOES_BASE_JURIDICO,
  ...CONCESSOES_BASE_SGI,
  ...CONCESSOES_BASE_RDO,
  ...CONCESSOES_BASE_MATERIAIS,
  ...CONCESSOES_BASE_CONTRATOS,
];

/**
 * O escape hatch declarado (T58, CAT-08).
 *
 * Uma entrada aqui é uma concessão que **não cabe em `Concessao`**, e o schema
 * não cobrir um caso é notícia ruim, não recurso. Por isso este mapa é contado em
 * teste: **crescer significa schema errado**, não hatch funcionando.
 *
 * As duas de hoje não cabem por motivos diferentes, e nenhum deles é "a
 * declaração ficou complicada":
 *
 * - **`biblioteca`** compara um campo do REGISTRO contra uma lista derivada do
 *   USUÁRIO (`user.todasPermissoes`). `Concessao.condicao` é valor fixo por papel;
 *   não há como guardar nele algo que muda a cada pessoa.
 * - **`acesso`** concede `editar` em `Permissao` a quem detém **qualquer** papel
 *   de nível `admin`. Não é concessão de um papel — é concessão de ter algum.
 *   Nenhum `PAPEIS_<D>.concessoes` a expressa sem repeti-la nos dezessete
 *   domínios.
 *
 * Cada uma recebe as entradas de que precisa e devolve regras já compiladas.
 */
export const CONCESSOES_CUSTOMIZADAS = {
  /** Recebe `user.todasPermissoes`: papéis, e-mail e empresas. */
  biblioteca: concessoesCustomizadasDeBiblioteca,
  /** Recebe os papéis canônicos já resolvidos do usuário. */
  acesso: concessoesCustomizadasDeAcesso,
} as const satisfies Record<string, (entradas: readonly string[]) => RegraCompilada[]>;

export { GRUPOS, type NomeGrupo } from "./grupos";
export type { Acao, Concessao, Dominio, NomePapel, Papel, Recurso } from "./vocabulario";
